import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["preProcessReport"];
import { useEffect, useState, useContext, useMemo } from 'react';
import { isReportWithPlugins, isCustomWidgetReport } from 'reporting-data/tsTypes/reportTypes';
import { getPluginName, getCustomWidgetType } from 'reporting-data/report/reportGetters';
import { getPlugin } from './PluginRegistry';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { isUngatedForCustomWidgetMigration } from './customWidgets/isUngatedForCustomWidgetMigration';
import { CUSTOM_WIDGET_MIGRATED } from './customWidgets/customWidgetConstants';
import { jsx as _jsx } from "react/jsx-runtime";
function getReportingPluginName(report, userInfo) {
  if (isReportWithPlugins(report)) {
    return getPluginName(report);
  }

  // Check whether a custom widget is in migration to reporting plugins
  // Initially we'd use widget name as report plugin - eventually the widget name prop
  // will be migrate to pluginName
  const customWidgetType = isCustomWidgetReport(report) && getCustomWidgetType(report) || '';
  if (customWidgetType && (CUSTOM_WIDGET_MIGRATED.includes(customWidgetType) || isUngatedForCustomWidgetMigration(customWidgetType, userInfo))) {
    return getCustomWidgetType(report);
  }
  return undefined;
}
function ReportingPluginsProvider(WrappedComponent) {
  const Wrapper = passthroughProps => {
    const {
      report
    } = passthroughProps;
    const userInfo = useContext(UserInfoContext);
    const reportPluginName = useMemo(() => getReportingPluginName(report, userInfo), [report, userInfo]);
    const [loading, setLoading] = useState(() => !!reportPluginName);
    const [handlers, setHandlers] = useState({});
    useEffect(() => {
      if (!reportPluginName) {
        return;
      }
      getPlugin(reportPluginName).then(({
        plugin
      }) => {
        setHandlers(plugin);
      }).catch(error => {
        // TODO: Log to NR
        throw new Error(`Failed to load reporting plugin: ${reportPluginName}. ${error.message}`, {
          cause: error
        });
      }).finally(() => {
        setLoading(false);
      });
    }, [report, reportPluginName]);

    // Extract the preProcessReport function from the handlers object, so that it won't be passed to the wrapped component
    const {
        preProcessReport
      } = handlers,
      restHandlers = _objectWithoutPropertiesLoose(handlers, _excluded);
    const preProcessedReport = useMemo(() => {
      var _preProcessReport;
      return (_preProcessReport = preProcessReport === null || preProcessReport === void 0 ? void 0 : preProcessReport(report)) !== null && _preProcessReport !== void 0 ? _preProcessReport : report;
    }, [report, preProcessReport]);
    if (loading) {
      return /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      });
    }
    return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, passthroughProps, restHandlers, {
      report: preProcessedReport
    }));
  };
  Wrapper.displayName = 'ReportingPluginsProvider';
  return Wrapper;
}
export default ReportingPluginsProvider;